<template>
  <div class="body">
    <canvas
      ref="threeCanvas"
      class="three-canvas"
      :class="mobileView ? 'mobileView' : ''"
    ></canvas>
    <div class="content-center" ref="loginForm">
      <router-view></router-view>
    </div>
  </div>
</template>

<script>
import * as THREE from "three";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader.js";

export default {
  name: "App",
  data() {
    return {
      mobileView: false,
    };
  },
  mounted() {
    this.initThreeJS();
    setTimeout(() => {
      this.mobileView = window.innerWidth < 1080;
    }, 1800);
  },
  methods: {
    initThreeJS() {
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x1b1a47);

      const camera = new THREE.PerspectiveCamera(
        45,
        window.innerWidth / window.innerHeight,
        0.1,
        100,
      );
      camera.position.set(0, 0, 15);

      const renderer = new THREE.WebGLRenderer({
        canvas: this.$refs.threeCanvas,
        antialias: true,
      });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

      scene.add(new THREE.AmbientLight(0xffffff, 1.2));

      let logoGroup;
      let mainGroup = new THREE.Group();
      scene.add(mainGroup);

      const loader = new SVGLoader();
      loader.load("https://identiti-hrms.tiburaglobal.com/public/harmony-final-default.svg", (data) => {
        logoGroup = new THREE.Group();

        data.paths.forEach((path, index) => {
          if (index === 0) return;

          const color =
            path.color?.getHexString() === "24a5de" ? 0x24a5de : 0xffffff;

          const material = new THREE.MeshBasicMaterial({
            color: color,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0,
          });

          const shapes = SVGLoader.createShapes(path);
          shapes.forEach((shape) => {
            const geometry = new THREE.ShapeGeometry(shape);
            const mesh = new THREE.Mesh(geometry, material);
            mesh.userData.pathIndex = index;

            mesh.userData.originalPosition = mesh.position.clone();
            mesh.userData.startPosition = {
              x: (Math.random() - 0.5) * 20,
              y: (Math.random() - 0.5) * 15,
              z: (Math.random() - 0.5) * 10,
            };

            logoGroup.add(mesh);
          });
        });

        logoGroup.scale.set(0.009, -0.009, 1);

        const box = new THREE.Box3().setFromObject(logoGroup);
        const center = box.getCenter(new THREE.Vector3());
        logoGroup.position.sub(center);

        mainGroup.add(logoGroup);
      });

      const lineGeometry = new THREE.BufferGeometry();

      const linePositions = new Float32Array(600);
      lineGeometry.setAttribute(
        "position",
        new THREE.BufferAttribute(linePositions, 3),
      );
      const lineMaterial = new THREE.LineBasicMaterial({
        color: 0x24a5de,
        linewidth: 0.001,
      });
      const line = new THREE.Line(lineGeometry, lineMaterial);
      line.position.y = -1.9;
      line.scale.set(0.7, 1, 1);
      mainGroup.add(line);

      const clock = new THREE.Clock();

      const animate = () => {
        requestAnimationFrame(animate);
        const t = clock.getElapsedTime();

        if (t > 1.8) {
          const moveProgress = Math.min(1, (t - 1.8) / 1.5);
          const easeMove = 1 - Math.pow(1 - moveProgress, 3);
          if (window.innerWidth < 1080) {
            mainGroup.position.x = 0 * easeMove;
          } else {
            mainGroup.position.x = -5 * easeMove;
          }

          this.$refs.loginForm.style.transform = "translateY(-50%) scale(1)";
          this.$refs.loginForm.style.opacity = "1";
        }

        if (logoGroup) {
          logoGroup.children.forEach((mesh) => {
            const pathIndex = mesh.userData.pathIndex;

            if (pathIndex >= 1 && pathIndex <= 6) {
              if (t < 1.5) {
                const tiburasProgress = t / 1.5;
                const easeProgress = 1 - Math.pow(1 - tiburasProgress, 3);

                mesh.material.opacity = tiburasProgress;
                mesh.position.x = THREE.MathUtils.lerp(
                  mesh.userData.startPosition.x,
                  mesh.userData.originalPosition.x,
                  easeProgress,
                );
                mesh.position.y = THREE.MathUtils.lerp(
                  mesh.userData.startPosition.y,
                  mesh.userData.originalPosition.y,
                  easeProgress,
                );
                mesh.position.z = THREE.MathUtils.lerp(
                  mesh.userData.startPosition.z,
                  mesh.userData.originalPosition.z,
                  easeProgress,
                );
              } else {
                mesh.material.opacity = 1;
              }
            } else if (pathIndex >= 7 && pathIndex <= 11) {
              if (t >= 0.1 && t < 2.1) {
                const nexusProgress = (t - 0.1) / 2;
                mesh.material.opacity = nexusProgress;
              } else if (t >= 2.1) {
                mesh.material.opacity = 1;
              } else {
                mesh.material.opacity = 0;
              }
            } else if (pathIndex >= 12) {
              if (t >= 0.2) {
                const typingProgress = Math.min(1, (t - 0.2) / 2);
                mesh.material.opacity = typingProgress;
                const scale = 0.95 + Math.min(1, typingProgress * 3) * 0.05;
                mesh.scale.set(scale, scale, scale);
              } else {
                mesh.material.opacity = 0;
              }
            } else {
              mesh.material.opacity = 0;
            }
          });
        }

        if (t >= 0.3 && t < 2.3) {
          let lineProgress = (t - 0.3) / 2;
          if (window.innerWidth > 1080) {
            lineProgress = (t - 0.2) / 2;
          }
          const positions = line.geometry.attributes.position.array;

          for (let i = 0; i < 200; i++) {
            const progress = Math.abs(i / 199 - 0.5) * 2;
            if (progress <= lineProgress) {
              positions[i * 3] = (i / 199 - 0.5) * 10;
              positions[i * 3 + 1] = 0;
              positions[i * 3 + 2] = 0;
            }
          }

          line.geometry.attributes.position.needsUpdate = true;
          line.geometry.setDrawRange(0, Math.floor((lineProgress + 0.5) * 200));
        }

        renderer.render(scene, camera);
      };

      animate();

      window.addEventListener("resize", () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      });
    },
  },
};
</script>

<style lang="scss">
@import "../src/scss/style.scss";

.body {
  background-color: #1b1a47;
  width: 100%;
  height: 100vh;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.three-canvas {
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.content-center {
  position: fixed;
  right: 150px;
  top: 50%;
  transform: translateY(-50%) scale(0);
  width: 400px;
  z-index: 1000;
  // transition: transform 3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 1s ease-out;
  // transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94),  opacity 0.3s ease-out;
  transition:
    transform 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    opacity 0.35s ease-out;
  opacity: 0;
}

@media (max-width: 1080px) {
  .content-center {
    left: 55% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
  }
  .mobileView {
    display: none !important;
  }
}

@media (max-width: 1396.36px) {
  .content-center {
    right: 7%;
    width: 355px;
  }
}
</style>
